/**
 * The Equipment table's writes (`equipment-page-table`, R9–R10, R14–R18) — the ONE place the order
 * of a rename, an identifier edit, an add and a removal lives.
 *
 * Every write goes through the step's save state (`begin` / `settle`), then one register re-read,
 * and is guarded against a twin switch mid-flight: after EVERY await, a twin changed since the send
 * drops the outcome — no re-read, no toast, no line. The re-read matters most: `reload` is bound to
 * the twin the write was issued for, and running it after a switch would load that twin's register
 * (and its write handle) into the shared store beside the new twin's plan. Every call is an existing route with its existing contract — `PATCH
 * twin-assets/:id`, `POST twin-assets` (the store's create), `POST twin-assets/discard` — bounded by
 * the transport's own timeout. Nothing here retries by itself: *Try again* is the person's, and a
 * re-sent add presents the same idempotency key (R18).
 *
 * Logs carry ids, counts and statuses, never names.
 */
import React, { useCallback, useRef } from 'react';
import { Icon, useToast } from '@repo/design-system';
import {
  isWriteOutcomeUnknown,
  updateTwinAsset,
  type TwinAssetResponse,
  type TwinAssetUpdateControls,
  type TwinAssetWriteFields,
} from '@repo/front-end-services';

import type { NameAnswer } from './equipmentRowEdit';

export interface DiscardOutcome {
  deleted: number[];
  alreadyGone: number[];
  failure: unknown;
}

export interface EquipmentWriteDeps {
  twinId: number | null;
  /** The twin on screen NOW, read after each await. */
  currentTwinId: () => number | null;
  baseUrl: string;
  /** A current token, bounded. */
  getToken: () => Promise<string>;
  begin: () => number;
  settle: (
    token: number | null,
    outcome: { ok: true } | { ok: false; reason?: string | null },
  ) => void;
  reload: () => void;
  /** The page's batched discard (`discardInBatches`, 500 a batch, stopping at the first failure). */
  discard: (ids: readonly number[]) => Promise<DiscardOutcome>;
  /** The store's create (`EquipmentCatalogStore.createRegisterAsset`). */
  create: (input: { name: string; idempotencyKey: string }) => Promise<TwinAssetResponse>;
  /** The store's `forgetRegisterWrites`: after a write made here, the next read is the server's. */
  forgetWrites: (ids: readonly number[]) => void;
  /** The register's ids as last read, for what *Try again* may still send. */
  registerIds: () => ReadonlySet<number>;
  /**
   * Of `ids`, the ones a removal may send NOW: none while the plan is unknown, and never a placed
   * row (R14). *Try again* asks again when it is pressed — the plan can change in between.
   */
  removable: (ids: readonly number[]) => number[];
  /** A *Try again* removal landed: the selection clears and focus moves to the title (R12, A2). */
  onRetryRemoved: () => void;
}

/** What a write in a cell came to: saved, or the line the field reopens with. */
export type CellWriteOutcome = { ok: true } | { ok: false; line: string };

export type AddOutcome =
  | { ok: true; created: TwinAssetResponse }
  /** `retry`: the outcome is unknown, so Enter again must present the same key. */
  | { ok: false; line: string; retry: boolean };

export type RemoveOutcome = 'removed' | 'failed' | 'stale';

/** Any one-row PATCH body: the asset card sends one field at a time (`equipment-page-asset-card`). */
export type RowWriteFields = TwinAssetWriteFields & TwinAssetUpdateControls;

/**
 * What a field write came to. `skipped`: its precondition no longer held when its turn came, so
 * nothing was sent. A refusal carries the server's error, for a caller that answers one specially.
 */
export type FieldWriteOutcome =
  | { ok: true }
  | { ok: false; skipped: true }
  | { ok: false; skipped?: false; line: string; error: unknown };

/** The precondition of a queued write failed: nothing was sent. */
const SKIPPED = Symbol('skipped');

/**
 * Writes in flight, chained per row (`equipment-page-asset-card`): the card saves one field per
 * blur, and two fields of one row left in quick succession would otherwise both be in flight, the
 * earlier answer and its re-read landing last. Queued, each PATCH is sent after the previous one
 * answered — the shape of the machine card's `queueRowWrite`. Exported for the selection bar's bulk
 * writes (`equipment-page-bulk-actions`, R9), so a bulk write and a cell edit of one row queue too.
 */
const rowWrites = new Map<number, Promise<unknown>>();
export const queueRowWrite = <T>(rowId: number, run: () => Promise<T>): Promise<T> => {
  const next = (rowWrites.get(rowId) ?? Promise.resolve()).then(run, run);
  const settled = next.then(
    () => undefined,
    () => undefined,
  );
  rowWrites.set(rowId, settled);
  void settled.then(() => {
    if (rowWrites.get(rowId) === settled) rowWrites.delete(rowId);
  });
  return next;
};

export const statusOf = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
};

export const reasonOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * The server's own sentence on an unknown-outcome error, where it sent one: a gateway status with a
 * readable body carries no `(503)` suffix on its message. Null for a timeout, an offline error or a
 * gateway answer with no readable body.
 */
const serverSentence = (error: unknown): string | null => {
  const status = statusOf(error);
  if (status !== 502 && status !== 503 && status !== 504) return null;
  const message = error instanceof Error ? error.message.trim() : '';
  return message && !/\((502|503|504)\)$/.test(message) ? message : null;
};

/** A PATCH whose outcome nobody can confirm (a timeout, a dropped connection, a gateway error). */
export const NOT_SURE = 'Not sure it was saved — the register has been read again';

/** A fresh key per add attempt, in the route's accepted shape (16–200 of [A-Za-z0-9._~:-]). */
export const newAddAttemptKey = (): string => {
  const random =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2).padEnd(10, '0')}`;
  return `add-${random}`;
};

export const useEquipmentRowWrites = (deps: EquipmentWriteDeps) => {
  const toast = useToast();
  // Latest deps through a ref: a toast's *Try again* can land after the page has re-rendered.
  const depsRef = useRef(deps);
  depsRef.current = deps;

  /**
   * One PATCH through the save state, queued behind the row's earlier writes; resolves the error,
   * null when it landed, or `SKIPPED` when `precondition` failed at its turn and nothing was sent.
   * `landed` runs inside the row's turn once the PATCH succeeded: the next write waits for it.
   */
  const patch = useCallback(
    (
      assetId: number,
      fields: RowWriteFields,
      precondition?: () => boolean,
      landed?: () => Promise<void>,
    ): Promise<unknown> => {
      // The twin the write is ISSUED for, read now — not when its turn in the row's queue comes, by
      // which time the deps may be another twin's and the guard below would compare it with itself.
      const issuedFor = depsRef.current.twinId;
      return queueRowWrite(assetId, async () => {
        const d = depsRef.current;
        // Switched twin while it waited its turn: nothing is sent, and nothing is said in the new
        // twin's save state.
        if (d.currentTwinId() !== issuedFor) return SKIPPED;
        if (precondition && !precondition()) return SKIPPED;
        const saveToken = d.begin();
        try {
          const token = await d.getToken();
          // Left the page or switched twin while the token was fetched: nothing is sent. The
          // callers' own check then drops the outcome, and this settle is outside the new twin's
          // scope, so it reports nothing (Copilot, PR #2157).
          if (d.currentTwinId() !== issuedFor) {
            d.settle(saveToken, { ok: true });
            return null;
          }
          await updateTwinAsset(d.baseUrl, token, assetId, fields);
          // Before the re-read: a row added earlier this session is otherwise re-read as added.
          d.forgetWrites([assetId]);
          d.settle(saveToken, { ok: true });
          if (landed) await landed().catch(() => undefined);
          return null;
        } catch (error) {
          d.settle(saveToken, { ok: false, reason: reasonOf(error, 'The change was not saved.') });
          return error ?? new Error('The change was not saved.');
        }
      });
    },
    [],
  );

  /**
   * Whether the twin the write was issued for is still the one on screen. The page answers `null`
   * once it has unmounted, so a late write never re-reads the old twin into the shared store; a
   * step change, which unmounts only this table, is not an exit (Copilot, PR #2157).
   */
  const stillOn = useCallback(
    (issuedFor: number | null) => depsRef.current.currentTwinId() === issuedFor,
    [],
  );

  const notSaved = useCallback(
    (reason: string) =>
      toast.show({
        tone: 'error',
        icon: React.createElement(Icon, { name: 'circle-exclamation' }),
        title: `Not saved: ${reason}`,
      }),
    [toast],
  );

  /**
   * Rename the holder FIRST, when the answer renames one, then the row (R9): names stay unique at
   * every step, and a failed holder rename sends nothing else.
   */
  const renameHolderFirst = useCallback(
    async (answer: NameAnswer): Promise<CellWriteOutcome | 'stale'> => {
      if (answer.kind !== 'holder') return { ok: true };
      const d = depsRef.current;
      const issuedFor = d.twinId;
      const error = await patch(answer.holder.assetId, { asset_name: answer.holder.to });
      if (error === null) return stillOn(issuedFor) ? { ok: true } : 'stale';
      console.warn('[equipment] rename failed', {
        twinId: issuedFor,
        assetId: answer.holder.assetId,
        step: 'holder',
        status: statusOf(error),
      });
      if (!stillOn(issuedFor)) return 'stale';
      d.reload();
      if (isWriteOutcomeUnknown(error)) {
        notSaved(NOT_SURE);
        return {
          ok: false,
          line: `Not sure ${answer.holder.from} was renamed, so nothing else was sent`,
        };
      }
      const reason = reasonOf(error, 'The change was not saved.');
      notSaved(reason);
      return {
        ok: false,
        line: `${answer.holder.from} could not be renamed, so nothing was saved: ${reason}`,
      };
    },
    [patch, notSaved, stillOn],
  );

  const rename = useCallback(
    async (rowId: number, answer: NameAnswer): Promise<CellWriteOutcome> => {
      const d = depsRef.current;
      const issuedFor = d.twinId;
      const first = await renameHolderFirst(answer);
      // A twin switched while the holder was being renamed: the row's write is not sent.
      if (first === 'stale') return { ok: true };
      if (!first.ok) return first;
      const error = await patch(rowId, { asset_name: answer.name });
      if (error !== null) {
        console.warn('[equipment] rename failed', {
          twinId: issuedFor,
          assetId: rowId,
          step: 'self',
          status: statusOf(error),
        });
      }
      if (!stillOn(issuedFor)) return { ok: true };
      d.reload();
      if (error === null) return { ok: true };
      const status = statusOf(error);
      if (isWriteOutcomeUnknown(error)) {
        notSaved(NOT_SURE);
        return { ok: false, line: NOT_SURE };
      }
      const reason = reasonOf(error, 'The change was not saved.');
      notSaved(reason);
      if (answer.kind === 'holder') {
        return {
          ok: false,
          line: `${answer.holder.from} was renamed ${answer.holder.to}, but this row could not be renamed: ${reason}`,
        };
      }
      return { ok: false, line: status === 400 ? reason : `Could not save: ${reason}` };
    },
    [renameHolderFirst, patch, notSaved, stillOn],
  );

  const setIdentifier = useCallback(
    async (rowId: number, value: string | null): Promise<CellWriteOutcome> => {
      const d = depsRef.current;
      const issuedFor = d.twinId;
      const error = await patch(rowId, { customer_asset_identifier: value });
      if (error !== null) {
        console.warn('[equipment] identifier edit failed', {
          twinId: issuedFor,
          assetId: rowId,
          status: statusOf(error),
        });
      }
      if (!stillOn(issuedFor)) return { ok: true };
      d.reload();
      if (error === null) return { ok: true };
      if (isWriteOutcomeUnknown(error)) {
        notSaved(NOT_SURE);
        return { ok: false, line: NOT_SURE };
      }
      const status = statusOf(error);
      const reason = reasonOf(error, 'The change was not saved.');
      notSaved(reason);
      return { ok: false, line: status === 400 ? reason : `Could not save: ${reason}` };
    },
    [patch, notSaved, stillOn],
  );

  /**
   * Any other one-row write — the asset card's fields, overrides, product and Not tracked
   * (`equipment-page-asset-card`, R6). The same save state, twin guard, queue and re-read as a
   * rename; the refusal is the CALLER's to show, under its own field, so nothing is toasted here.
   * `precondition` is checked at the write's turn in the row's queue (a product Undo's guard).
   */
  const setFields = useCallback(
    async (
      rowId: number,
      fields: RowWriteFields,
      options?: {
        precondition?: () => boolean;
        /**
         * Resolves once the register carries this write. The row's queue holds the next write
         * until it does, so that write's precondition reads this one's result, not the row before
         * it (a product Undo queued behind a newer change; Copilot, PR #2177).
         */
        carried?: () => Promise<void>;
      },
    ): Promise<FieldWriteOutcome> => {
      const d = depsRef.current;
      const issuedFor = d.twinId;
      let reread = false;
      const carried = options?.carried;
      const landed = carried
        ? async () => {
            if (!stillOn(issuedFor)) return;
            reread = true;
            d.reload();
            await carried();
          }
        : undefined;
      const error = await patch(rowId, fields, options?.precondition, landed);
      if (error === SKIPPED) return { ok: false, skipped: true };
      if (error !== null) {
        console.warn('[equipment] field write failed', {
          twinId: issuedFor,
          assetId: rowId,
          fields: Object.keys(fields),
          status: statusOf(error),
        });
      }
      // Switched twin meanwhile: the outcome belongs to a twin no longer on screen, and is said as
      // nothing — never as a success the caller would announce (a *Product changed* toast).
      if (!stillOn(issuedFor)) return { ok: false, skipped: true };
      if (!reread) d.reload();
      if (error === null) return { ok: true };
      if (isWriteOutcomeUnknown(error)) {
        /*
         * Every 502/503/504 is an unknown outcome, by the transport's rule — the importer's own 503
         * after a partial write is that shape. Where the server SAID something (a 503 with a
         * readable body: overrides not yet on this register, the catalogue unreadable), its
         * sentence follows, so the operator is not left with "not sure" alone.
         */
        const said = serverSentence(error);
        return { ok: false, line: said ? `${NOT_SURE}. ${said}` : NOT_SURE, error };
      }
      return { ok: false, line: reasonOf(error, 'The change was not saved.'), error };
    },
    [patch, stillOn],
  );

  /**
   * Add one asset by name (R17–R18), under the CALLER's attempt key: the add row mints one per
   * name typed and presents it again when Enter is pressed again after an unknown outcome.
   */
  const add = useCallback(
    async (answer: NameAnswer, idempotencyKey: string): Promise<AddOutcome> => {
      const d = depsRef.current;
      const issuedFor = d.twinId;
      const changed = {
        ok: false as const,
        line: 'The twin changed, so nothing was added.',
        retry: false,
      };
      const first = await renameHolderFirst(answer);
      if (first === 'stale') return changed;
      if (!first.ok) return { ok: false, line: first.line, retry: false };
      const saveToken = d.begin();
      try {
        const created = await d.create({ name: answer.name, idempotencyKey });
        d.settle(saveToken, { ok: true });
        // Created against the twin it was issued for; on another twin there is nothing to show.
        if (!stillOn(issuedFor)) return changed;
        d.reload();
        return { ok: true, created };
      } catch (error) {
        const reason = reasonOf(error, 'The asset could not be added.');
        d.settle(saveToken, { ok: false, reason });
        console.warn('[equipment] add failed', { twinId: issuedFor, status: statusOf(error) });
        if (!stillOn(issuedFor)) return changed;
        d.reload();
        if (isWriteOutcomeUnknown(error)) {
          // Said aloud too: the line under the field is a description, which is not announced (A4).
          toast.show({
            tone: 'warning',
            icon: React.createElement(Icon, { name: 'circle-exclamation' }),
            title: 'Not sure it was added',
          });
          return {
            ok: false,
            line: 'Not sure it was added — press Enter to try again',
            retry: true,
          };
        }
        notSaved(reason);
        if (answer.kind === 'holder') {
          return {
            ok: false,
            line: `${answer.holder.from} was renamed ${answer.holder.to}, but the new asset was not added: ${reason}`,
            retry: false,
          };
        }
        return { ok: false, line: reason, retry: false };
      }
    },
    [renameHolderFirst, notSaved, stillOn, toast],
  );

  /** Remove `ids` — never a placed row; the caller filters (R14, R16). */
  const remove = useCallback(
    async (ids: readonly number[]): Promise<RemoveOutcome> => {
      const d = depsRef.current;
      const issuedFor = d.twinId;
      const k = ids.length;
      const saveToken = d.begin();
      let outcome: DiscardOutcome;
      try {
        outcome = await d.discard(ids);
      } catch (failure) {
        outcome = { deleted: [], alreadyGone: [], failure };
      }
      // Every id the server acknowledged, partial batches included, before the re-read.
      d.forgetWrites([...outcome.deleted, ...outcome.alreadyGone]);
      const failed = outcome.failure !== undefined && outcome.failure !== null;
      const reason = reasonOf(outcome.failure, 'The rows could not be removed.');
      d.settle(saveToken, failed ? { ok: false, reason } : { ok: true });
      const status = statusOf(outcome.failure);
      if (failed) {
        console.warn('[equipment] remove failed', {
          twinId: issuedFor,
          count: k,
          status,
          deleted: outcome.deleted.length,
        });
      }
      if (!stillOn(issuedFor)) return 'stale';
      d.reload();
      if (!failed) {
        toast.show({
          tone: 'neutral',
          icon: React.createElement(Icon, { name: 'trash-can' }),
          title: `${k} removed from the register`,
        });
        return 'removed';
      }
      const done = outcome.deleted.length + outcome.alreadyGone.length;
      // A batch is all-or-nothing on the server: a 409 means a SAVED plan places a row the plan
      // on screen does not show. Re-sending would meet the same refusal, so there is no Try again;
      // as Remove all says, the person reloads to see that plan (this page has no plan re-read).
      if (status === 409 && done === 0) {
        toast.show({
          tone: 'error',
          icon: React.createElement(Icon, { name: 'circle-exclamation' }),
          title: `Nothing was removed: ${reason}`,
          detail:
            'Another saved copy of the plan still places them — reload the page to see it before ' +
            'trying again.',
        });
        return 'failed';
      }
      // A timeout or a dropped connection may have removed rows unacknowledged: say so, not that
      // they could not be removed — the re-read just issued shows which went (Copilot, PR #2157).
      if (isWriteOutcomeUnknown(outcome.failure)) {
        toast.show({
          tone: 'warning',
          icon: React.createElement(Icon, { name: 'circle-exclamation' }),
          title: 'Not sure they were removed — the register has been read again',
        });
        return 'failed';
      }
      const tryAgain = {
        label: 'Try again',
        onAction: () => {
          if (retrying.current) return;
          // Only what is still on the register after the re-read, and — asked again now, since the
          // plan may have changed — never a placed row, never while the plan is unknown.
          const deleted = new Set(outcome.deleted);
          const present = depsRef.current.registerIds();
          const rest = depsRef.current.removable(
            ids.filter(id => present.has(id) && !deleted.has(id)),
          );
          if (rest.length === 0) {
            toast.show({
              tone: 'neutral',
              icon: React.createElement(Icon, { name: 'circle-info' }),
              title: 'Nothing left that can be removed',
            });
            return;
          }
          retrying.current = true;
          void removeRef.current(rest).then(result => {
            retrying.current = false;
            if (result === 'removed') depsRef.current.onRetryRemoved();
          });
        },
      };
      toast.show({
        tone: 'error',
        icon: React.createElement(Icon, { name: 'circle-exclamation' }),
        title:
          done > 0
            ? `${done} of ${k} removed; ${k - done} could not be: ${reason}`
            : `${k} could not be removed: ${reason}`,
        actions: [tryAgain],
      });
      return 'failed';
    },
    [toast, stillOn],
  );
  const removeRef = useRef(remove);
  // One *Try again* at a time: a second press while it runs would send the same discard twice.
  const retrying = useRef(false);
  removeRef.current = remove;

  return { rename, setIdentifier, setFields, add, remove };
};
